// Cave84 — Edit Wine Modal (owner-only)
// Inline edit for an existing wine's core fields: cuvée, style, appellation.
// Mirrors AddWineModal styling. Persists via useUpdateWine (Firestore patch).

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import type { Wine, WineStyle } from '@cave84/shared-types';
import type { WineUpdateInput } from '@/services/wines';
import { useUpdateWine } from '@/hooks/useWines';
import { useAppellationSearch, useCreateAppellation } from '@/hooks/useAppellations';

const inputClass =
  'w-full bg-surface-2 border border-border-medium rounded px-3 py-2 text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:border-gold transition-colors';
const btnPrimary =
  'px-4 py-2 rounded bg-claret text-text-primary text-xs font-medium hover:bg-claret/80 transition-colors disabled:opacity-50';
const btnSecondary =
  'px-4 py-2 rounded border border-border-medium text-text-secondary text-xs font-medium hover:border-border-strong hover:text-text-primary transition-colors';

function FormField({ label, error, children }: {
  label: string; error?: string; children: React.ReactNode;
}) {
  return (
    <div>
      <label className="smallcaps text-text-tertiary block mb-1.5" style={{ fontSize: 11 }}>{label}</label>
      {children}
      {error && <p className="text-claret text-xs mt-1">{error}</p>}
    </div>
  );
}

const STYLES: WineStyle[] = ['red', 'white', 'rose', 'sparkling', 'fortified', 'sweet', 'orange'];
const STYLE_LABELS: Record<WineStyle, string> = {
  red: 'Red', white: 'White', rose: 'Rosé', sparkling: 'Sparkling',
  fortified: 'Fortified', sweet: 'Sweet', orange: 'Orange',
};
const STYLE_COLORS: Record<WineStyle, string> = {
  red: '#C49255', white: '#C49255', rose: '#D4768A', sparkling: '#7E9A98',
  fortified: '#8E663A', sweet: '#8FB57C', orange: '#C49255',
};

interface EditWineFormValues {
  cuvee_name: string;
  style: WineStyle;
}

export function EditWineModal({
  wine,
  currentAppellationName,
  onClose,
  onSaved,
}: {
  wine: Wine;
  currentAppellationName?: string;
  onClose: () => void;
  onSaved?: () => void;
}) {
  const updateWine = useUpdateWine();
  const createAppellation = useCreateAppellation();

  // Appellation: prefilled with the wine's current appellation. If the user
  // edits the search field we treat the existing selection as cleared until
  // they pick a result (or keep the typed text, which creates a minimal one).
  const [appellationTerm, setAppellationTerm] = useState(currentAppellationName ?? '');
  const [selectedAppellationId, setSelectedAppellationId] = useState(wine.appellation_id);
  const [selectedAppellationName, setSelectedAppellationName] = useState(currentAppellationName ?? '');
  const appellationResults = useAppellationSearch(appellationTerm);

  const { register, handleSubmit, setValue, watch, formState: { errors, isSubmitting } } =
    useForm<EditWineFormValues>({
      defaultValues: { cuvee_name: wine.cuvee_name ?? '', style: wine.style ?? 'red' },
    });

  const selectedStyle = watch('style');

  const pickAppellation = (id: string, name: string) => {
    setSelectedAppellationId(id);
    setSelectedAppellationName(name);
    setAppellationTerm(name);
  };

  const onSubmit = async (values: EditWineFormValues) => {
    let appellationId = selectedAppellationId;

    // No saved selection but free text typed → create a minimal appellation.
    if (!appellationId && appellationTerm.trim()) {
      const app = await createAppellation.mutateAsync({
        name: appellationTerm.trim(),
        hierarchy_path: appellationTerm.trim(),
        classification_level: 'regional',
        country: '',
        region: '',
      });
      appellationId = app.id;
    }

    if (!appellationId) { return; }

    const patch: WineUpdateInput = {
      cuvee_name: values.cuvee_name.trim(),
      style: values.style,
      appellation_id: appellationId,
    };

    await updateWine.mutateAsync({ id: wine.id, patch });
    onSaved?.();
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} aria-hidden="true" />
      <div className="relative z-10 w-full max-w-lg">
        <div className="rounded-xl border border-border-medium bg-surface-1 shadow-2xl">
          <div className="flex items-center justify-between px-5 py-4 border-b border-border-subtle">
            <h2 className="text-sm font-semibold text-text-primary">Edit wine</h2>
            <button type="button" onClick={onClose}
              className="text-text-muted hover:text-text-primary text-lg leading-none transition-colors"
              aria-label="Close">&times;</button>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="px-5 py-4 space-y-4">
            <FormField label="Cuvée name" error={errors.cuvee_name?.message}>
              <input
                {...register('cuvee_name', { required: 'Required', maxLength: { value: 120, message: 'Max 120' } })}
                className={inputClass}
                placeholder="e.g. Chambertin, Chablis Premier Cru Montée de Tonnerre" />
            </FormField>

            <div>
              <span className="smallcaps text-text-tertiary block mb-1.5" style={{ fontSize: 11 }}>Style</span>
              <div className="flex flex-wrap gap-2">
                {STYLES.map(s => (
                  <button key={s} type="button" onClick={() => setValue('style', s)}
                    className={`px-3 py-1.5 rounded border text-xs font-medium transition-all ${selectedStyle === s ? 'border-transparent' : 'border-border-medium text-text-secondary bg-surface-2 hover:border-border-strong'}`}
                    style={selectedStyle === s ? { backgroundColor: `${STYLE_COLORS[s]}22`, borderColor: STYLE_COLORS[s], color: STYLE_COLORS[s] } : {}}>
                    {STYLE_LABELS[s]}
                  </button>
                ))}
              </div>
            </div>

            <FormField label="Appellation" error={!selectedAppellationId && !appellationTerm.trim() ? 'Required' : undefined}>
              <input
                type="search"
                value={appellationTerm}
                onChange={e => { setAppellationTerm(e.target.value); setSelectedAppellationId(''); setSelectedAppellationName(''); }}
                placeholder="Search appellation…"
                className={inputClass} />
              {appellationTerm.trim().length >= 2 && appellationResults.length > 0 && !selectedAppellationId && (
                <div className="mt-1 border border-border-subtle rounded overflow-hidden max-h-40 overflow-y-auto">
                  {appellationResults.slice(0, 6).map(a => (
                    <button key={a.id} type="button" onClick={() => pickAppellation(a.id, a.name)}
                      className="w-full text-left px-3 py-2 text-sm text-text-primary hover:bg-surface-2 border-b border-border-subtle last:border-0 transition-colors">
                      {a.name}
                      <span className="text-text-muted text-xs ml-2">{a.hierarchy_path}</span>
                    </button>
                  ))}
                </div>
              )}
              {selectedAppellationName && (
                <p className="text-gold text-xs mt-1">Selected: {selectedAppellationName}</p>
              )}
            </FormField>

            {(updateWine.error || createAppellation.error) && (
              <p className="text-claret text-xs">{(updateWine.error ?? createAppellation.error)?.message}</p>
            )}

            <div className="flex gap-2 pt-1">
              <button type="button" onClick={onClose} className={btnSecondary}>Cancel</button>
              <button type="submit" disabled={isSubmitting || updateWine.isPending} className={`${btnPrimary} flex-1`}>
                {isSubmitting || updateWine.isPending ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
